import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, History, AlertTriangle, Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import {
  getContract,
  createContract,
  updateContract,
  revertContractToStripe,
  getContractAudit,
  ContractDetails,
  ContractAuditEvent,
  CreateContractPayload,
  UpdateContractPayload,
} from '@/services/contractApi';
import { ContractPlanInput } from '@/services/contractApi';
import { listPlans, PlanTier, getEnterprise, Enterprise } from '@/services/organizationApi';
import { ContractPlansEditor } from '@/components/contract/ContractPlansEditor';
import { ContractTermsForm } from '@/components/contract/ContractTermsForm';
import { CreationPlansEditor } from '@/components/contract/CreationPlansEditor';

const getErrorMessage = (err: unknown): string => {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { error?: string; details?: { code?: string } } } }).response;
    return response?.data?.error || '';
  }
  if (err && typeof err === 'object' && 'message' in err) {
    return (err as { message: string }).message;
  }
  return 'Erreur inconnue';
};

interface FormState {
  planSlug: string;
  teamQuantity: number;
  contractReference: string;
  contractSignedAt: string;
  contractStartsAt: string;
  contractEndsAt: string;
  contractNotes: string;
  reason: string;
}

const emptyForm: FormState = {
  planSlug: 'solve',
  teamQuantity: 5,
  contractReference: '',
  contractSignedAt: '',
  contractStartsAt: '',
  contractEndsAt: '',
  contractNotes: '',
  reason: '',
};

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('fr-FR');
  } catch {
    return iso;
  }
}

function isoToInputDate(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toISOString().split('T')[0];
  } catch {
    return '';
  }
}

function eventTypeLabel(type: string): string {
  switch (type) {
    case 'contract.created': return 'Contrat créé';
    case 'contract.seats_changed': return 'Quota modifié';
    case 'contract.plan_seats_changed': return 'Mix de plans modifié';
    case 'contract.renewed': return 'Renouvellement';
    case 'contract.terms_changed': return 'Termes modifiés';
    case 'contract.reverted_to_stripe': return 'Bascule vers Stripe';
    default: return type;
  }
}

/**
 * Résume une liste de plans (issue de before/after_state d'un event audit)
 * en une chaine compacte type "5 lead + 12 solve + 3 detect". Retourne "—"
 * si la forme attendue n'est pas trouvée (defensive).
 */
function summarizePlansState(state: Record<string, unknown> | null | undefined): string {
  if (!state || !Array.isArray(state.plans) || state.plans.length === 0) return '—';
  return state.plans
    .map((p) => {
      const slug = (p as { planSlug?: string }).planSlug ?? '?';
      const seats = (p as { seatsPurchased?: number }).seatsPurchased ?? 0;
      return `${seats} ${slug}`;
    })
    .join(' + ');
}

function eventTypeBadgeClass(type: string): string {
  switch (type) {
    case 'contract.created': return 'bg-emerald-100 text-emerald-700';
    case 'contract.seats_changed': return 'bg-amber-100 text-amber-700';
    case 'contract.plan_seats_changed': return 'bg-amber-100 text-amber-700';
    case 'contract.terms_changed': return 'bg-blue-100 text-blue-700';
    case 'contract.renewed': return 'bg-blue-100 text-blue-700';
    case 'contract.reverted_to_stripe': return 'bg-rose-100 text-rose-700';
    default: return 'bg-gray-100 text-gray-700';
  }
}

export default function ContractPage() {
  const { enterpriseId } = useParams<{ enterpriseId: string }>();
  const navigate = useNavigate();

  const [enterprise, setEnterprise] = useState<Enterprise | null>(null);
  const [contract, setContract] = useState<ContractDetails | null>(null);
  const [auditEvents, setAuditEvents] = useState<ContractAuditEvent[]>([]);
  const [plans, setPlans] = useState<PlanTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reverting, setReverting] = useState(false);

  const [form, setForm] = useState<FormState>(emptyForm);
  // État dédié à la création multi-plan (UI tableau de plans). En mode édition,
  // la modification du mix passe par ContractPlansEditor (composant séparé).
  const [creationPlans, setCreationPlans] = useState<ContractPlanInput[]>([
    { planSlug: 'lead', teamQuantity: 5 },
  ]);
  // Total prévu remonté en live par ContractPlansEditor (édition) ou
  // CreationPlansEditor (création). null = pas encore initialisé → on retombe
  // sur le quota actuel du contrat.
  const [previewTotalSeats, setPreviewTotalSeats] = useState<number | null>(null);
  const [revertReason, setRevertReason] = useState('');
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);

  // Reset le preview à chaque (re)chargement de contrat pour repartir du quota
  // serveur (évite qu'un edit non sauvegardé d'une session précédente persiste).
  useEffect(() => {
    setPreviewTotalSeats(null);
  }, [contract?.subscription?.id]);

  /**
   * Active le bouton "Enregistrer" uniquement si :
   *   - en création : tous les champs obligatoires sont remplis + raison >= 3 char
   *   - en édition  : raison >= 3 char + au moins un champ effectivement modifié
   *                   (ignore la raison seule, sinon on activerait à chaque saisie
   *                   d'audit même sans changement métier)
   */
  const isDirty = useMemo(() => {
    const reasonOk = form.reason.trim().length >= 3;
    if (!contract) {
      // Création : au moins un plan valide (slug non vide, quantité >= min_seats).
      // min_seats vient de plan_tiers (mig 099) : 5 pour lead/solve/detect, 1 pour solo/beta.
      const plansOk = creationPlans.length > 0
        && creationPlans.every((p) => {
          if (!p.planSlug.trim()) return false;
          const minSeats = plans.find((pl) => pl.slug === p.planSlug)?.min_seats ?? 1;
          return p.teamQuantity >= minSeats;
        });
      return Boolean(
        reasonOk
        && plansOk
        && form.contractReference.trim()
        && form.contractSignedAt
        && form.contractEndsAt,
      );
    }
    if (!reasonOk) return false;
    const sub = contract.subscription;
    const sameStarts = isoToInputDate(sub.contract_starts_at) === form.contractStartsAt;
    const sameSigned = isoToInputDate(sub.contract_signed_at) === form.contractSignedAt;
    const sameEnds = isoToInputDate(sub.contract_ends_at) === form.contractEndsAt;
    const sameNotes = (sub.contract_notes || '') === form.contractNotes;
    const sameRef = (sub.contract_reference || '') === form.contractReference;
    const sameQuota = sub.team_quantity === form.teamQuantity;
    const samePlan = (sub.plan_slug || '') === form.planSlug;
    return !(sameStarts && sameSigned && sameEnds && sameNotes && sameRef && sameQuota && samePlan);
  }, [form, contract, creationPlans, plans]);

  /**
   * Liste exhaustive des champs qui empêchent le bouton "Créer le contrat" /
   * "Enregistrer les modifications" d'être actif. Affichée en dessous du
   * bouton quand il est désactivé, pour lever l'ambiguité « j'ai tout rempli ».
   */
  const missingFields = useMemo(() => {
    const missing: string[] = [];
    if (!contract) {
      if (creationPlans.length === 0) {
        missing.push('au moins un plan');
      } else {
        const invalid = creationPlans.find((p) => {
          if (!p.planSlug.trim()) return true;
          const min = plans.find((pl) => pl.slug === p.planSlug)?.min_seats ?? 1;
          return p.teamQuantity < min;
        });
        if (invalid) {
          const tier = plans.find((pl) => pl.slug === invalid.planSlug);
          const min = tier?.min_seats ?? 1;
          missing.push(`quota de ${tier?.name ?? invalid.planSlug} ≥ ${min}`);
        }
      }
      if (!form.contractReference.trim()) missing.push('référence du contrat');
      if (!form.contractSignedAt) missing.push('date de signature');
      if (!form.contractEndsAt) missing.push('date de fin');
    }
    const r = form.reason.trim();
    if (r.length === 0) missing.push('raison (audit)');
    else if (r.length < 3) missing.push('raison ≥ 3 caractères');
    return missing;
  }, [contract, form, creationPlans, plans]);

  const loadAll = useCallback(async () => {
    if (!enterpriseId) return;
    setLoading(true);
    try {
      const [ent, details, audit, plansList] = await Promise.all([
        getEnterprise(enterpriseId),
        getContract(enterpriseId),
        getContractAudit(enterpriseId).catch(() => ({ events: [] })),
        listPlans(),
      ]);
      setEnterprise(ent);
      setContract(details);
      setAuditEvents(audit.events);
      setPlans(plansList);

      if (details) {
        setForm({
          planSlug: details.subscription.plan_slug || 'solve',
          teamQuantity: details.subscription.team_quantity,
          contractReference: details.subscription.contract_reference || '',
          contractSignedAt: isoToInputDate(details.subscription.contract_signed_at),
          contractStartsAt: isoToInputDate(details.subscription.contract_starts_at),
          contractEndsAt: isoToInputDate(details.subscription.contract_ends_at),
          contractNotes: details.subscription.contract_notes || '',
          reason: '',
        });
      } else {
        setForm(emptyForm);
      }
    } catch (err) {
      toast.error(`Chargement échoué : ${getErrorMessage(err)}`);
    } finally {
      setLoading(false);
    }
  }, [enterpriseId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleSubmit = async () => {
    if (!enterpriseId) return;
    if (!form.reason || form.reason.trim().length < 3) {
      toast.error('La raison est obligatoire (3 caractères minimum)');
      return;
    }
    setSaving(true);
    try {
      if (contract) {
        // Update
        const payload: UpdateContractPayload = {
          reason: form.reason,
        };
        if (form.teamQuantity !== contract.subscription.team_quantity) {
          payload.teamQuantity = form.teamQuantity;
        }
        // Changement de plan : le sélecteur expose le slug, mais le backend
        // attend l'UUID (planTierId). On résout depuis la liste plans chargée.
        const selectedPlan = plans.find((p) => p.slug === form.planSlug);
        if (selectedPlan && selectedPlan.id !== contract.subscription.plan_tier_id) {
          payload.planTierId = selectedPlan.id;
        }
        if (form.contractReference !== (contract.subscription.contract_reference || '')) {
          payload.contractReference = form.contractReference;
        }
        const signedISO = form.contractSignedAt ? new Date(form.contractSignedAt).toISOString() : '';
        const existingSignedISO = contract.subscription.contract_signed_at || '';
        if (signedISO && signedISO !== existingSignedISO) {
          payload.contractSignedAt = signedISO;
        }
        const startsISO = form.contractStartsAt ? new Date(form.contractStartsAt).toISOString() : null;
        const existingStartsISO = contract.subscription.contract_starts_at;
        if (startsISO !== existingStartsISO) {
          payload.contractStartsAt = startsISO;
        }
        const endsISO = form.contractEndsAt ? new Date(form.contractEndsAt).toISOString() : '';
        const existingEndsISO = contract.subscription.contract_ends_at || '';
        if (endsISO && endsISO !== existingEndsISO) {
          payload.contractEndsAt = endsISO;
        }
        if (form.contractNotes !== (contract.subscription.contract_notes || '')) {
          payload.contractNotes = form.contractNotes || null;
        }
        await updateContract(enterpriseId, payload);
        toast.success('Contrat mis à jour');
      } else {
        // Create — validation precise champ par champ (le check global pre-existant
        // remontait une erreur generique meme apres saisie correcte si un seul
        // champ etait vide ; on pointe maintenant le champ manquant).
        const missing: string[] = [];
        if (!form.contractReference.trim()) missing.push('Référence');
        if (!form.contractSignedAt) missing.push('Date de signature');
        if (!form.contractEndsAt) missing.push('Date de fin');
        if (creationPlans.length === 0) missing.push('Au moins un plan');
        if (missing.length > 0) {
          toast.error(`Champ(s) manquant(s) : ${missing.join(', ')}`);
          setSaving(false);
          return;
        }
        // Detection doublons côté UI (backend revalidera) pour message immédiat.
        const slugSet = new Set<string>();
        for (const p of creationPlans) {
          if (slugSet.has(p.planSlug)) {
            toast.error(`Plan dupliqué dans le mix : ${p.planSlug}`);
            setSaving(false);
            return;
          }
          slugSet.add(p.planSlug);
        }
        // Validation min_seats côté UI (cohérent avec mig 099 et validation backend).
        for (const p of creationPlans) {
          const tier = plans.find((pl) => pl.slug === p.planSlug);
          const min = tier?.min_seats ?? 1;
          if (p.teamQuantity < min) {
            toast.error(`Le plan ${tier?.name ?? p.planSlug} requiert au minimum ${min} siège${min > 1 ? 's' : ''}.`);
            setSaving(false);
            return;
          }
        }
        const payload: CreateContractPayload = {
          plans: creationPlans.map((p) => ({ planSlug: p.planSlug, teamQuantity: p.teamQuantity })),
          contractReference: form.contractReference.trim(),
          contractSignedAt: new Date(form.contractSignedAt).toISOString(),
          contractStartsAt: form.contractStartsAt ? new Date(form.contractStartsAt).toISOString() : null,
          contractEndsAt: new Date(form.contractEndsAt).toISOString(),
          contractNotes: form.contractNotes || null,
          reason: form.reason,
        };
        await createContract(enterpriseId, payload);
        toast.success('Contrat créé');
      }
      setForm({ ...form, reason: '' });
      await loadAll();
    } catch (err) {
      toast.error(`Échec : ${getErrorMessage(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = async () => {
    if (!enterpriseId) return;
    if (!revertReason || revertReason.trim().length < 3) {
      toast.error('La raison est obligatoire (3 caractères minimum)');
      return;
    }
    setReverting(true);
    try {
      await revertContractToStripe(enterpriseId, revertReason);
      toast.success('Bascule vers Stripe effectuée');
      setShowRevertConfirm(false);
      setRevertReason('');
      await loadAll();
    } catch (err) {
      toast.error(`Échec : ${getErrorMessage(err)}`);
    } finally {
      setReverting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  // Quota effectif pour l'affichage : preview live si l'admin a modifié les
  // plans, sinon valeur serveur. Permet à la card de réagir à chaque ajout /
  // modif / suppression de plan avant même le clic sur "Enregistrer le mix".
  const serverQuota = contract?.usage.quota ?? 0;
  const currentUsage = contract?.usage.current ?? 0;
  const effectiveQuota = previewTotalSeats ?? serverQuota;
  const usagePct = effectiveQuota > 0
    ? Math.round((currentUsage / effectiveQuota) * 100)
    : 0;
  const usageColor = usagePct >= 100 ? 'bg-rose-500' : usagePct >= 90 ? 'bg-amber-500' : 'bg-emerald-500';
  const usageBadge = usagePct >= 100 ? 'text-rose-700 bg-rose-50' : usagePct >= 90 ? 'text-amber-700 bg-amber-50' : 'text-emerald-700 bg-emerald-50';
  const isPreviewDirty = previewTotalSeats !== null && previewTotalSeats !== serverQuota;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/enterprises')}
          className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <FileText className="w-6 h-6 text-indigo-600" />
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-gray-900">Contrat — {enterprise?.name ?? '...'}</h1>
          <p className="text-sm text-gray-500">
            Mode de facturation négocié hors Stripe. Quota fixe, audit complet de toute modification.
          </p>
        </div>
        {contract && (
          <button
            onClick={() => setShowRevertConfirm(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-rose-700 bg-rose-50 hover:bg-rose-100 rounded-lg border border-rose-200"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Basculer vers Stripe
          </button>
        )}
      </div>

      {/* Usage card (only when in contract mode) */}
      {contract && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-900">Utilisation des sièges</h2>
            <span className={`text-xs font-medium px-2 py-0.5 rounded ${usageBadge}`}>
              {usagePct}% utilisé
            </span>
          </div>
          <div className="flex items-baseline gap-2 mb-3">
            <span className="text-3xl font-semibold text-gray-900">{currentUsage}</span>
            <span className="text-sm text-gray-500">/ {effectiveQuota} sièges</span>
            {isPreviewDirty && (
              <span className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded ml-2">
                prévu — {effectiveQuota > serverQuota ? '+' : ''}{effectiveQuota - serverQuota} vs actuel ({serverQuota}), non enregistré
              </span>
            )}
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
            <div
              className={`h-full ${usageColor} transition-all`}
              style={{ width: `${Math.min(100, usagePct)}%` }}
            />
          </div>
          {usagePct >= 90 && (
            <p className="mt-3 text-sm text-amber-700 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                {usagePct >= 100
                  ? 'Cap atteint — les nouvelles invitations sont refusées tant que le quota n\'est pas augmenté.'
                  : 'Seuil de 90% franchi — anticipez un avenant pour éviter le blocage.'}
              </span>
            </p>
          )}
        </div>
      )}

      {/* Multi-plan editor — only when contract already exists. Permet
          d'ajouter / modifier / supprimer des plans dans le mix avec workflow
          downgrade (modale désactivation préalable). En création, le mix est
          choisi directement via CreationPlansEditor ci-dessous. */}
      {contract && (
        <ContractPlansEditor
          enterpriseId={enterpriseId!}
          contract={contract}
          availablePlans={plans}
          onReload={loadAll}
          onTotalSeatsChange={setPreviewTotalSeats}
        />
      )}

      {!contract && (
        <CreationPlansEditor
          plans={creationPlans}
          setPlans={setCreationPlans}
          availablePlans={plans}
        />
      )}

      <ContractTermsForm
        form={form}
        setForm={setForm}
        contract={contract}
        plans={plans}
        isDirty={isDirty}
        saving={saving}
        onSubmit={handleSubmit}
        hidePlanFields={!contract}
        missingFields={missingFields}
      />


      {/* Audit history */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <History className="w-4 h-4 text-gray-500" />
          Historique d'audit
          <span className="text-xs text-gray-500 font-normal">({auditEvents.length})</span>
        </h2>
        {auditEvents.length === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center">Aucun événement enregistré.</p>
        ) : (
          <div className="space-y-2">
            {auditEvents.map((ev) => (
              <div key={ev.id} className="p-3 rounded-lg border border-gray-100 hover:bg-gray-50">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded ${eventTypeBadgeClass(ev.event_type)}`}>
                        {eventTypeLabel(ev.event_type)}
                      </span>
                      <span className="text-xs text-gray-500">{formatDateTime(ev.created_at)}</span>
                    </div>
                    {ev.reason && (
                      <p className="text-sm text-gray-700 mb-1">{ev.reason}</p>
                    )}
                    <p className="text-xs text-gray-500">
                      par <span className="font-medium">{ev.acting_admin_email || 'inconnu'}</span>
                      {ev.ip_address && <span className="text-gray-400"> · {ev.ip_address}</span>}
                    </p>
                    {ev.event_type === 'contract.seats_changed' && ev.before_state && ev.after_state && (
                      <p className="text-xs text-gray-600 mt-1">
                        {String((ev.before_state as { team_quantity?: number }).team_quantity ?? '?')} → {String((ev.after_state as { team_quantity?: number }).team_quantity ?? '?')} sièges
                      </p>
                    )}
                    {ev.event_type === 'contract.plan_seats_changed' && ev.after_state && (
                      <p className="text-xs text-gray-600 mt-1">
                        {summarizePlansState(ev.before_state)} → {summarizePlansState(ev.after_state)}
                      </p>
                    )}
                    {ev.event_type === 'contract.created' && ev.after_state && Array.isArray((ev.after_state as { plans?: unknown[] }).plans) && (
                      <p className="text-xs text-gray-600 mt-1">
                        Mix initial : {summarizePlansState(ev.after_state)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Revert confirmation modal */}
      {showRevertConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4 w-full">
            <h3 className="text-lg font-semibold mb-2">Basculer vers Stripe</h3>
            <p className="text-sm text-gray-600 mb-4">
              Cette enterprise repassera en mode Stripe. Le quota négocié sera supprimé et la facturation reprendra via Stripe.
            </p>
            <label className="block text-xs font-medium text-gray-700 mb-1">Raison (audit) *</label>
            <input
              type="text"
              value={revertReason}
              onChange={(e) => setRevertReason(e.target.value)}
              placeholder="ex. Le client passe en paiement direct"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-4"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowRevertConfirm(false); setRevertReason(''); }}
                className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={handleRevert}
                disabled={reverting}
                className="flex items-center gap-2 px-3 py-1.5 bg-rose-600 text-white text-sm rounded-lg hover:bg-rose-700 disabled:bg-gray-300"
              >
                {reverting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Confirmer la bascule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
